import { inject, Injectable, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import {
  ConfirmDialogComponent,
  HlmDialogOpener,
  type ConfirmDialogContext,
  type ConfirmDialogResult,
} from '@lite-workspace/shared/helm-ui/dialog';
import { isRetouchedPhoto } from '@lite-workspace/dto-fotoclass-photo';
import { firstValueFrom, take } from 'rxjs';
import { ProjectsFacade } from '../domain/application/projects.facade';
import { albumProductId } from '../domain/application/product-geometry-refresh.service';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { needsName } from '../domain/utils/project.utils';
import { isUsableAlbum } from '@lite-workspace/dto-fotoclass-book';
import type { FC_PhotoBookDTO, Person } from '../domain/entities/project.models';
import {
  BulkDownloadAlbumsDialogComponent,
  type BulkDownloadAlbum,
  type BulkDownloadAlbumsDialogContext,
  type PrintPaymentScope,
} from './bulk-download-albums-dialog.component';
import {
  PrintFilesDialogComponent,
  printFilesData,
  type PrintFilesData,
  type PrintFilesDialogContext,
  type PrintFilesDialogResult,
} from './print-files-dialog.component';
import { SALES_DATA } from '../../upsale/domain/infrastructure/http-sales.data';
import { OrganizerShareLauncherService } from './organizer-share-launcher.service';
import { DemoDownloadGuardService } from './demo-download-guard.service';
import {
  NotFilledNamesDialogComponent,
  type NotFilledNamesDialogContext,
  type NotFilledNamesResult,
} from './not-filled-names-dialog.component';

type BulkDownloadResult = string[] | 'billing-error' | undefined;

type BulkRefusal =
  | { kind: 'demo' }
  | { kind: 'roster-failed' }
  | { kind: 'names'; unfilled: Person[] }
  | { kind: 'empty' }
  | { kind: 'unapproved' }
  | { kind: 'retouch'; waiting: number };

const PAYMENT_FAILED_MESSAGE = 'Не удалось выполнить оплату — попробуйте ещё раз';
const PAYMENT_UNKNOWN_MESSAGE = 'Не удалось проверить оплату — попробуйте ещё раз';
const SETTINGS_UNKNOWN_MESSAGE = 'Настройки проекта не загрузились — попробуйте ещё раз';

/** The one «Скачать альбомы» flow (analytics → names gate → retouch warning → bulk dialog), shared by every trigger
 *  surface (top-bar button, mobile nav drawer). Resolves once the loads finish and a dialog opens. */
@Injectable({ providedIn: 'root' })
export class ProjectDownloadLauncherService {
  private readonly facade = inject(ProjectsFacade);
  private readonly opener = inject(HlmDialogOpener);
  private readonly shareLauncher = inject(OrganizerShareLauncherService);
  private readonly toast = inject(ToastService);
  private readonly demoGuard = inject(DemoDownloadGuardService);
  private readonly router = inject(Router);
  private readonly sales = inject(SALES_DATA);

  /** `skipRetouch` — the photographer already chose «Скачать всё равно» on the retouch warning. */
  open(vcr: ViewContainerRef, projectId: string, skipRetouch = false): void {
    // The dialog opens on the click itself; the demo/names gates resolve behind its loading skeleton
    // and, when they refuse, swap it for the matching gate instead of delaying the first paint.
    const prepared = this.prepareBulk(projectId, skipRetouch);
    const ref = this.opener.openWithCheck<BulkDownloadAlbumsDialogContext, Awaited<typeof prepared>, BulkDownloadResult>(
      BulkDownloadAlbumsDialogComponent,
      vcr,
      { projectId, albums: prepared.then(prep => (prep.kind === 'ready' ? prep.albums : null)) },
      prepared,
      (prep, openRef) => {
        if (prep.kind === 'ready') return;
        openRef.close(undefined);
        this.refuse(vcr, projectId, prep);
      },
    );
    ref.closed$.pipe(take(1)).subscribe(result => {
      if (result === 'billing-error') {
        this.toast.error(PAYMENT_FAILED_MESSAGE);
      } else if (Array.isArray(result) && result.length > 0) {
        this.facade.downloadAlbumsArchive(projectId, result);
      }
    });
  }

  /** «Скачать файлы печати»: two archives (albums + copies, print orders) under one unpaid switch; the bulk
   *  demo gate, names checked on approved-album owners only. */
  openPrintFiles(vcr: ViewContainerRef, projectId: string): void {
    const prepared = this.preparePrint(projectId);
    const ref = this.opener.openWithCheck<PrintFilesDialogContext, Awaited<typeof prepared>, PrintFilesDialogResult | undefined>(
      PrintFilesDialogComponent,
      vcr,
      { data: prepared.then(prep => (prep.kind === 'ready' ? prep.data : null)) },
      prepared,
      (prep, openRef) => {
        if (prep.kind === 'ready') return;
        openRef.close(undefined);
        if (prep.kind === 'bills-failed') this.toast.error(PAYMENT_UNKNOWN_MESSAGE);
        else if (prep.kind === 'settings-failed') this.toast.error(SETTINGS_UNKNOWN_MESSAGE);
        else this.refuse(vcr, projectId, prep);
      },
    );
    ref.closed$.pipe(take(1)).subscribe(result => {
      if (!result) return;

      if (result.archive === 'prints') {
        void this.downloadPrintPhotos(projectId, result.includeUnpaid);
        return;
      }

      void prepared.then(async prep => {
        if (prep.kind !== 'ready' || !(await this.ensurePaid(vcr, projectId, prep.data.albums, 'project'))) return;
        this.facade.downloadAlbumsArchive(
          projectId,
          prep.data.albums.map(album => album.id),
          { withCopies: true, includeUnpaid: result.includeUnpaid },
        );
      });
    });
  }

  /** Print-order archive (`<student>/<size>/`); no charge gate. Every entry (dialog, «Покупки» row) comes here. */
  async downloadPrintPhotos(projectId: string, includeUnpaid: boolean, personId?: string): Promise<void> {
    if (!(await this.demoGuard.ensureNotDemo(undefined, projectId))) return;
    this.facade.downloadPrintPhotosArchive(projectId, includeUnpaid, personId);
  }

  /** Платёжный шлюз печати для ОДНОГО альбома: `true` — экспорт может идти дальше.
   *
   *  Списание проектное, разовое и общее на все альбомы, поэтому одиночный макет проходит ТОТ ЖЕ
   *  диалог, что «Скачать альбомы» — без оплаты бэкенд отвечает на экспорт 400. Уже оплаченный проект
   *  не показывает ничего: читатель выбрал пункт меню, а не «может быть», и лишний экран между
   *  нажатием и скачиванием ему нечего сказать. */
  async ensurePrintPaid(
    vcr: ViewContainerRef,
    projectId: string,
    album: FC_PhotoBookDTO,
    owner: Person | null,
  ): Promise<boolean> {
    return this.ensurePaid(vcr, projectId, [this.toBulkAlbum(album, owner ?? undefined)], 'album');
  }

  private async ensurePaid(
    vcr: ViewContainerRef,
    projectId: string,
    albums: BulkDownloadAlbum[],
    scope: PrintPaymentScope,
  ): Promise<boolean> {
    let charged: boolean;
    try {
      charged = await this.facade.isProjectCharged(projectId);
    } catch {
      // Fail closed, как и bulk: экспорт неоплаченного проекта всё равно вернётся отказом.
      this.toast.error(PAYMENT_UNKNOWN_MESSAGE);
      return false;
    }

    if (charged) return true;

    const ref = this.opener.open<BulkDownloadAlbumsDialogContext, BulkDownloadResult>(BulkDownloadAlbumsDialogComponent, vcr, {
      projectId,
      albums,
      scope,
    });
    const result = await firstValueFrom(ref.closed$.pipe(take(1)), { defaultValue: undefined });
    if (result === 'billing-error') {
      this.toast.error(PAYMENT_FAILED_MESSAGE);
      return false;
    }

    return Array.isArray(result) && result.length > 0;
  }

  private refuse(vcr: ViewContainerRef, projectId: string, prep: BulkRefusal): void {
    switch (prep.kind) {
      case 'demo':
        this.demoGuard.showDemoNotice(vcr);
        return;
      case 'names':
        this.openNamesGate(vcr, projectId, prep.unfilled);
        return;
      case 'roster-failed':
        this.toast.error('Не удалось проверить имена — повторите');
        return;
      case 'empty':
        this.toast.info('В проекте пока нет альбомов');
        return;
      case 'unapproved':
        this.toast.info('Утверждённых альбомов пока нет');
        return;
      case 'retouch':
        this.openRetouchGate(vcr, projectId, prep.waiting);
    }
  }

  /** Demo gate of «Скачать альбомы» and its names gate narrowed to approved-album owners;
   *  the retouch reminder stays with that entry. */
  private async preparePrint(
    projectId: string,
  ): Promise<BulkRefusal | { kind: 'bills-failed' } | { kind: 'settings-failed' } | { kind: 'ready'; data: PrintFilesData }> {
    const settingsKnown: boolean = !!this.facade.baseSettingsByProject()[projectId] && !!this.facade.upsaleByProject()[projectId];
    const [prep, bills, settingsRead] = await Promise.all([
      this.prepareBulk(projectId, true, true),
      this.sales.list(projectId).catch((): null => null),
      // The copy gate reads the album product and the copy formats: without them every copy passes as the album.
      settingsKnown
        ? true
        : this.facade.loadProjectBaseSettings(projectId).then(
            () => true,
            () => false,
          ),
    ]);
    if (prep.kind !== 'ready') return prep;
    // Fail closed: «ещё не оплачены» counted off a missing read would be a wrong fact, not a missing one.
    if (bills === null) return { kind: 'bills-failed' };
    if (!settingsRead) return { kind: 'settings-failed' };
    const names = new Map(prep.roster.map(person => [person.id, person.name ?? 'Имя не задано']));
    const productId = this.facade.baseSettingsByProject()[projectId]?.photoBook?.printProductId || undefined;
    const copyFormats = this.facade.upsaleByProject()[projectId]?.goods.copies?.extra ?? [];
    const data = printFilesData(prep.albums, bills, names, productId, copyFormats);
    return data.albums.length > 0 ? { kind: 'ready', data } : { kind: 'unapproved' };
  }

  private async prepareBulk(
    projectId: string,
    skipRetouch: boolean,
    approvedOnly = false,
  ): Promise<BulkRefusal | { kind: 'ready'; albums: BulkDownloadAlbum[]; roster: readonly Person[] }> {
    if (await this.demoGuard.isDemo(projectId)) return { kind: 'demo' };

    // Read beside the albums, not after them: the warning must not add a round trip to every download.
    const waiting = skipRetouch ? Promise.resolve(0) : this.retouchWaiting(projectId);
    const [, roster] = await Promise.all([this.facade.loadAlbums(projectId), this.facade.fullPersonRoster(projectId)]);
    // `null` is refused rather than waved through: a download whose names were never checked is exactly
    // the archive of «Имя не задано» vignettes the gate exists to prevent.
    if (roster === null) return { kind: 'roster-failed' };

    const albums = this.buildBulkAlbums(projectId, roster);
    // Print files carry approved albums only: a nameless person without one never reaches the archive.
    const printed = approvedOnly ? new Set(albums.filter(album => album.approved).map(album => album.personId)) : null;
    const unfilled = roster.filter(person => needsName(person) && (!printed || printed.has(person.id)));
    if (unfilled.length > 0) return { kind: 'names', unfilled };

    if (albums.length === 0) return { kind: 'empty' };

    const waitingCount = await waiting;
    return waitingCount > 0 ? { kind: 'retouch', waiting: waitingCount } : { kind: 'ready', albums, roster };
  }

  /** Photos of the retouch scope still waiting, counted only once retouch has started (≥1 tagged):
   *  a project that never retouched has nothing to remind about. `0` = no warning. */
  private async retouchWaiting(projectId: string): Promise<number> {
    try {
      await Promise.all([this.facade.loadProjectBaseSettings(projectId), this.facade.loadRetouchScope(projectId)]);
    } catch {
      // Fail open: the warning is a reminder, an unread scope must not stand between the photographer and the archive.
      return 0;
    }

    if (!this.facade.retouchEnabled()) return 0;
    const scope = this.facade.retouchScope();
    const waiting = scope.filter(photo => !isRetouchedPhoto(photo)).length;
    return waiting < scope.length ? waiting : 0;
  }

  private openRetouchGate(vcr: ViewContainerRef, projectId: string, waiting: number): void {
    const ref = this.opener.open<ConfirmDialogContext, ConfirmDialogResult | undefined>(ConfirmDialogComponent, vcr, {
      title: `В альбомах ${waiting} фото без ретуши`,
      message: 'Их можно отретушировать сейчас или скачать альбомы как есть.',
      iconTone: 'warn',
      cancelLabel: null,
      secondaryLabel: 'Скачать всё равно',
      confirmLabel: 'Показать фото',
    });
    ref.closed$.pipe(take(1)).subscribe(result => {
      if (result === 'secondary') {
        this.open(vcr, projectId, true);
      } else if (result === true) {
        void this.router.navigate(['/projects', projectId, 'retouch'], { queryParams: { check: 1 } });
      }
    });
  }

  /** The names gate for a single album: every person the layout PRINTS, owner included. Awaits one
   *  full-model read, so callers must not have committed to the download before it answers. */
  async ensureAlbumNamesFilled(vcr: ViewContainerRef, projectId: string, albumId: string): Promise<boolean> {
    return this.ensureNamesFilled(vcr, projectId, await this.facade.albumPrintedPersons(projectId, albumId));
  }

  /** The names gate for every download entry point: `false` means the caller must stop — either the gate
   *  opened or the person list could not be read at all. Both the project-wide button and a single album
   *  card go through here so the rule and its wording can never drift apart.
   *
   *  `null` is refused rather than waved through: a download whose names were never checked is exactly the
   *  archive of «Имя не задано» vignettes this gate exists to prevent. */
  ensureNamesFilled(vcr: ViewContainerRef, projectId: string, persons: readonly Person[] | null): persons is readonly Person[] {
    if (persons === null) {
      this.toast.error('Не удалось проверить имена — повторите');
      return false;
    }

    const unfilled = persons.filter(needsName);
    if (unfilled.length === 0) return true;

    this.openNamesGate(vcr, projectId, unfilled);
    return false;
  }

  private openNamesGate(vcr: ViewContainerRef, projectId: string, persons: Person[]): void {
    const ref = this.opener.open<NotFilledNamesDialogContext, NotFilledNamesResult | undefined>(
      NotFilledNamesDialogComponent,
      vcr,
      { persons },
    );
    ref.closed$.pipe(take(1)).subscribe(result => {
      if (result === 'fill') {
        // «Заполнить имена» ведёт на таб «Лица» — там имена и заполняются, а не в отдельном мастере
        // поверх закрытого диалога.
        void this.router.navigate(['/projects', projectId, 'faces']);
      } else if (result === 'share') {
        void this.shareLauncher.open(vcr, projectId, { organizerOnly: true });
      }
    });
  }

  private buildBulkAlbums(projectId: string, roster: readonly Person[]): BulkDownloadAlbum[] {
    // Labelled off the full roster, not the grid's page: a student past the first page would otherwise be
    // offered for download under «Имя не задано» right after the gate confirmed their name is filled.
    const persons = new Map(roster.map(person => [person.id, person]));
    // The master is the project's template, not a student's book: it never takes part in the approval
    // chain, so counting it as «not approved» would hold the whole archive hostage to an album that can
    // never be signed.
    const masterAlbumId = this.facade.baseSettingsByProject()[projectId]?.photoBook?.masterAlbumId ?? null;
    return (
      this.facade
        .albums()
        // A model-less claim stub is not an album: counting it as «not approved» would block the whole
        // print archive on a book that does not exist yet.
        .filter(album => isUsableAlbum(album))
        // Шаблон отсеивается по собственному признаку строки: указатель masterAlbumId едет отдельным
        // запросом и на чужих вкладках может быть ещё не загружен — а строка-шаблон в списке печати
        // блокирует весь архив (её нельзя утвердить). Фильтр по указателю остаётся ради легаси-мастера.
        .filter(album => album.personId !== null)
        .filter(album => album.id !== masterAlbumId)
        // «Без альбома» on the faces page keeps the leftover book but drops it from the Albums page: a
        // print file never needs its approval either, so it neither counts nor blocks here.
        .filter(album => persons.get(album.personId as string)?.withBook !== false)
        .map(album => this.toBulkAlbum(album, album.personId ? persons.get(album.personId) : undefined))
    );
  }

  private toBulkAlbum(album: FC_PhotoBookDTO, person: Person | undefined): BulkDownloadAlbum {
    return {
      id: album.id,
      name: person?.name ?? 'Имя не задано',
      avatar: person?.avatar ?? null,
      approved: album.status === 'approved',
      personId: album.personId,
      printProductId: albumProductId(album),
    };
  }
}
